import React, { useEffect, useReducer, useRef } from 'react';

import _ from 'lodash';
import * as Yup from 'yup';
import md5 from 'md5';
import { Link, Prompt } from 'react-router-dom';
import { Button, FormControlLabel, Grid, Paper } from '@material-ui/core';
import { Switch, TextField, useTheme } from '@material-ui/core';
import { MdAdd } from 'react-icons/md';

import {
  VerticalTimeline,
  VerticalTimelineElement,
} from 'react-vertical-timeline-component';

import API from 'utils/api';
import { useSources } from 'hooks';
import { ButtonContainer, ErrorDialog, Form, PageTitle } from 'components';
import { Progress, SectionTitle } from 'components';
import { backLink, classes, scrollIntoView } from 'utils';
import { Preview } from 'routes/Dashboard/Template';

import EditMessageDialog from './EditMessageDialog';
import TriggerSelector from './TriggerSelector';
import ResponseDialog from './ResponseDialog';
import TimeLineElement from './TimeLineElement';

import styles from './MessageFlowEditor.module.scss';
import 'react-vertical-timeline-component/style.min.css';

const initialValues = {
  name: '',
  trigger: [],
  active: false,
};

const validate = Yup.object().shape({
  name: Yup.string().required('Required'),
  trigger: Yup.array().of(Yup.object({})),
  active: Yup.boolean(),
});

const initialState = props => {
  const id = _.get(props, 'match.params.id');
  const autoScroll = 0;

  return {
    id,
    status: id ? 'init' : 'active',
    messages: [],
    editIndex: -1,
    autoScroll,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SHOW_ADD_MESSAGE_DIALOG':
      return !state.add ? { ...state, add: true } : state;

    case 'SHOW_EDIT_MESSAGE_DIALOG':
      return state.editIndex !== action.payload
        ? { ...state, editIndex: action.payload }
        : state;

    case 'SAVE_MESSAGE':
      return {
        ...state,
        editIndex: -1,
        add: false,
        messages:
          state.editIndex > -1
            ? state.messages.map((it, index) =>
                index !== state.editIndex ? it : action.payload
              )
            : [...state.messages, action.payload],
        autoScroll: state.add ? (state.autoScroll ? 0 : 1) : state.autoScroll,
      };

    case 'CLOSE_DIALOG':
      return {
        ...state,
        editIndex: -1,
        add: false,
        preview: null,
        response: null,
      };

    case 'DELETE_MESSAGE':
      state.messages.splice(action.payload, 1);
      return { ...state };

    case 'INIT':
      return {
        ...state,
        status: action.payload ? 'active' : 'error',
        initialValues: _.pick(action.payload, Object.keys(initialValues)),
        messages: _.get(action.payload, 'messages'),
        initialMessages: _.cloneDeep(_.get(action.payload, 'messages')),
        autoScroll: 0,
      };

    case 'SHOW_PREVIEW_INTERACTION': {
      const msg =
        typeof action.payload === 'number' ? state.messages[action.payload] : null;

      return {
        ...state,
        preview: msg
          ? { id: _.get(msg, 'body.templateId'), type: _.get(msg, 'body.type') }
          : null,
      };
    }

    case 'SHOW_RESPONSE_DIALOG':
      return { ...state, response: action.payload };

    case 'CHANGE_POSITION': {
      const { currentPos, newPos } = action.payload;
      const { messages } = state;

      messages.splice(newPos, 0, messages.splice(currentPos, 1)[0]);
      return { ...state, messages: messages.map(x => x), highlightPos: newPos };
    }

    case 'SET_HIGHLIGHT_EL':
      return state.highlightEl !== action.payload
        ? { ...state, highlightEl: action.payload }
        : state;

    default:
      return state;
  }
};

const MessageFlowEditor = props => {
  const [state, dispatch] = useReducer(reducer, props, initialState);

  const theme = useTheme();
  const scrollRef = useRef(null);
  const sources = useSources();

  useEffect(() => {
    if (state.id) {
      API.getMessageFlow(
        state.id,
        { parseTags: true, parseMessages: true },
        sources
      ).then(({ data }) => dispatch({ type: 'INIT', payload: data }));
    }
  }, [sources, state.id]);

  useEffect(() => {
    scrollIntoView(scrollRef.current);
  }, [state.autoScroll]);

  useEffect(() => {
    scrollIntoView(state.highlightEl);
  }, [state.highlightEl]);

  if (state.status === 'init') {
    return <Progress />;
  }

  if (state.status === 'error') {
    return (
      <ErrorDialog
        open={true}
        message="Could not load flow!"
        onClose={() => props.history.push(backLink(props))}
      />
    );
  }

  const onSubmit = async (values, { setSubmitting, setStatus }) => {
    const data = {
      ...values,
      messages: state.messages.map(it =>
        it.type === 'interaction' ? { ...it, body: _.pick(it.body, 'templateId') } : it
      ),
      trigger: (values.trigger || []).map(it => it.value.id),
    };

    dispatch({ type: 'SHOW_RESPONSE_DIALOG', payload: {} });

    const response = await (state.id
      ? API.updateMessageFlow(state.id, data, sources)
      : API.createMessageFlow(data, sources));

    if (parseInt(response.status / 100) === 2) {
      setStatus('submitted');
    }

    setSubmitting(false);
    dispatch({ type: 'SHOW_RESPONSE_DIALOG', payload: response });
  };

  const renderFields = ({ fieldProps, checkboxProps }) => (
    <Paper className={styles.paper}>
      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <TextField {...fieldProps('name')} />
            <FormControlLabel
              control={<Switch {...checkboxProps('active')} />}
              label="Active"
            />
          </Grid>
          <Grid item xs={8}>
            <TriggerSelector {...fieldProps('trigger', 'Triggers')} />
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderTimeline = () => {
    const color = theme.palette.primary.main;
    const iconStyle = { background: '#fff', color, boxShadow: `0 0 0 4px ${color}` };
    const highlight = { fontWeight: 'bold', color };

    return (
      <Paper className={classes(styles.paper, styles.flowContainer)}>
        <div className={styles.header}>
          <SectionTitle title="Messages" infoStyle={{ maxWidth: 250 }}>
            Click the <span style={highlight}>+</span> button to add new messages to the
            flow. The messages will be sent in the defined sequence and the time specified
            in each one is <span style={highlight}>relative</span> to the previous
            message.
          </SectionTitle>
        </div>

        <div className={styles.body}>
          <VerticalTimeline layout="1-column-left" lineColor={color}>
            {state.messages.map((it, index) => (
              <TimeLineElement
                ref={
                  state.highlightPos === index
                    ? r => (r ? dispatch({ type: 'SET_HIGHLIGHT_EL', payload: r }) : null)
                    : null
                }
                key={`${index}:${md5(JSON.stringify(it))}`}
                data={it}
                index={index}
                numMessages={state.messages.length}
                dispatch={dispatch}
                highlight={state.highlightPos === index}
              />
            ))}

            <VerticalTimelineElement
              className={styles.addButton}
              iconClassName={styles.addIcon}
              iconStyle={iconStyle}
              icon={<MdAdd />}
              iconOnClick={() => dispatch({ type: 'SHOW_ADD_MESSAGE_DIALOG' })}
            />
          </VerticalTimeline>

          <div className={styles.bottomGuide} ref={scrollRef} />
        </div>
      </Paper>
    );
  };

  return (
    <div className={styles.editor}>
      <PageTitle>Message Flow Editor</PageTitle>

      <Form
        className={styles.mainForm}
        validationSchema={validate}
        initialValues={state.initialValues || initialValues}
        onSubmit={onSubmit}
      >
        {form => (
          <>
            {renderFields(form)}
            {renderTimeline()}

            <ButtonContainer>
              <Button component={Link} to={backLink(props)}>
                cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={form.isSubmitting}
              >
                save
              </Button>
            </ButtonContainer>

            <Prompt
              when={
                form.status !== 'submitted' &&
                (form.dirty ||
                  !_.isEqual(state.messages || [], state.initialMessages || []))
              }
              message="You are leaving the page with unsaved fields. Are you sure you want to leave?"
            />
          </>
        )}
      </Form>

      <EditMessageDialog
        open={!!(state.add || state.editIndex > -1)}
        data={state.editIndex > -1 ? state.messages[state.editIndex] : null}
        onClose={() => dispatch({ type: 'CLOSE_DIALOG' })}
        onChange={data => dispatch({ type: 'SAVE_MESSAGE', payload: data })}
      />

      <Preview
        open={!!state.preview}
        onClose={() => dispatch({ type: 'CLOSE_DIALOG' })}
        type={_.get(state, 'preview.type')}
        id={_.get(state, 'preview.id')}
      />

      <ResponseDialog
        response={state.response}
        onClose={success =>
          success
            ? props.history.push(backLink(props))
            : dispatch({ type: 'CLOSE_DIALOG' })
        }
      />
    </div>
  );
};

export default MessageFlowEditor;
